import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ClaimEmailWithRecipients, EmailDirection } from "@/api/generated/client/models";
import { format } from "date-fns";
import DOMPurify from "dompurify";
import { Mail, User, Calendar, XCircle, ArrowRight, EyeOff, Reply, Loader2, ChevronDown, Download } from "lucide-react";

interface EmailDetailModalProps {
    email: ClaimEmailWithRecipients | null;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onReply?: (params: {
        to: Array<{ email: string; name?: string }>;
        subject: string;
        bodyText: string;
        inReplyToGmailThreadId?: string;
    }) => Promise<void>;
    isReplying?: boolean;
    startWithReplyForm?: boolean;
    defaultReplyBody?: string;
    onReplyFormOpenChange?: (isOpen: boolean) => void;
    onDownloadAttachment?: (attachmentId: number, filename: string) => Promise<void>;
}

function formatAttachmentSize(sizeBytes: number): string {
    if (sizeBytes < 1024) return `${sizeBytes} B`;
    if (sizeBytes < 1024 * 1024) return `${(sizeBytes / 1024).toFixed(0)} KB`;
    return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function EmailDetailModal({ email, open, onOpenChange, onReply, isReplying = false, startWithReplyForm = false, defaultReplyBody, onReplyFormOpenChange, onDownloadAttachment }: EmailDetailModalProps) {
    const [showReplyForm, setShowReplyForm] = useState(false);
    const [replyBody, setReplyBody] = useState("");
    const [downloadingAttachmentId, setDownloadingAttachmentId] = useState<number | null>(null);
    const autoOpenedReplyThisOpenRef = useRef(false);

    const isReceived = email?.direction === EmailDirection.Inbound;
    const isSentOutbound = email?.direction === EmailDirection.Outbound && email.status === "sent";
    const toRecipients = email?.recipients?.filter((r) => r.recipientType === "to") || [];
    const ccRecipients = email?.recipients?.filter((r) => r.recipientType === "cc") || [];
    const bccRecipients = email?.recipients?.filter((r) => r.recipientType === "bcc") || [];
    const senderAddress = email?.fromEmail.trim().toLowerCase() ?? "";
    const replyRecipients = isReceived
        ? email?.fromEmail
            ? [{ email: email.fromEmail, ...(email.fromName ? { name: email.fromName } : {}) }]
            : []
        : isSentOutbound
            ? toRecipients
            .filter((recipient) => recipient.email.trim() && recipient.email.trim().toLowerCase() !== senderAddress)
            .map((recipient) => ({ email: recipient.email, ...(recipient.name ? { name: recipient.name } : {}) }))
            : [];
    const replyRecipientLabel = replyRecipients
        .map((recipient) => recipient.name ? `${recipient.name} <${recipient.email}>` : recipient.email)
        .join(", ");
    const canReply = Boolean(onReply && replyRecipients.length > 0);

    useEffect(() => {
        if (!open) {
            autoOpenedReplyThisOpenRef.current = false;
            return;
        }

        if (autoOpenedReplyThisOpenRef.current) return;

        if (startWithReplyForm && canReply) {
            setShowReplyForm(true);
            autoOpenedReplyThisOpenRef.current = true;
        }
    }, [open, startWithReplyForm, canReply]);

    useEffect(() => {
        if (!open || !showReplyForm || !defaultReplyBody?.trim()) return;
        setReplyBody((prevBody) => (prevBody.trim() ? prevBody : defaultReplyBody));
    }, [open, showReplyForm, defaultReplyBody]);

    useEffect(() => {
        onReplyFormOpenChange?.(open && showReplyForm);
    }, [open, showReplyForm, onReplyFormOpenChange]);

    if (!email) return null;

    // Preserve an existing reply prefix case-insensitively ("Re:", "RE:", "re:")
    // so replies aren't double-prefixed into subjects like "Re: RE: …".
    const replySubject = email.subject && /^re:/i.test(email.subject) ? email.subject : `Re: ${email.subject || "(No Subject)"}`;

    const handleReply = async () => {
        if (!onReply || !replyBody.trim()) return;

        try {
            await onReply({
                to: replyRecipients,
                subject: replySubject,
                bodyText: replyBody,
                inReplyToGmailThreadId: email.gmailThreadId ?? undefined,
            });
            setReplyBody("");
            setShowReplyForm(false);
            handleOpenChange(false);
        } catch {
            // Error handled/toasted by parent — keep form open
        }
    };

    const handleOpenChange = (open: boolean) => {
        if (!open) {
            setShowReplyForm(false);
            setReplyBody("");
        }
        onOpenChange(open);
    };

    const handleDownloadAttachment = async (attachmentId: number, filename: string) => {
        if (!onDownloadAttachment) return;
        setDownloadingAttachmentId(attachmentId);
        try {
            await onDownloadAttachment(attachmentId, filename);
        } catch {
            return;
        } finally {
            setDownloadingAttachmentId(null);
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
             <DialogContent className="sm:max-w-[700px] max-h-[85dvh] md:max-h-[80vh] p-0 overflow-y-auto">
                <DialogDescription className="sr-only">
                    {canReply ? "Email details and reply options." : isReceived ? "Received email message details." : "Sent email message details."}
                </DialogDescription>
                <div className="w-full">
                    <div className="w-0 min-w-full px-6 py-6">
                    <div className="space-y-6 overflow-hidden">
                        {/* Metadata Section */}
                        <div className="grid gap-4">
                            {/* Received: show From (who sent it to you) */}
                            {isReceived && (
                                <div className="flex items-start gap-3">
                                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
                                        <User className="h-4 w-4 text-muted-foreground" />
                                    </div>
                                    <div className="flex-1 min-w-0 space-y-1">
                                        <p className="text-sm font-medium text-muted-foreground">From</p>
                                        <p className="text-sm break-words">{email.fromName ? `${email.fromName} <${email.fromEmail}>` : email.fromEmail}</p>
                                    </div>
                                </div>
                            )}

                            {/* Sent: show To (who you sent it to) */}
                            {!isReceived && toRecipients.length > 0 && (
                                <div className="flex items-start gap-3">
                                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
                                        <Mail className="h-4 w-4 text-muted-foreground" />
                                    </div>
                                    <div className="flex-1 min-w-0 space-y-1">
                                        <p className="text-sm font-medium text-muted-foreground">To</p>
                                        <p className="text-sm break-words">{toRecipients.map((r) => r.name ? `${r.name} <${r.email}>` : r.email).join(", ")}</p>
                                    </div>
                                </div>
                            )}

                            {/* CC Recipients */}
                            {ccRecipients.length > 0 && (
                                <div className="flex items-start gap-3">
                                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
                                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                                    </div>
                                    <div className="flex-1 min-w-0 space-y-1">
                                        <p className="text-sm font-medium text-muted-foreground">CC</p>
                                        <p className="text-sm break-words">{ccRecipients.map((r) => r.email).join(", ")}</p>
                                    </div>
                                </div>
                            )}

                            {/* BCC Recipients */}
                            {bccRecipients.length > 0 && (
                                <div className="flex items-start gap-3">
                                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
                                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                                    </div>
                                    <div className="flex-1 min-w-0 space-y-1">
                                        <p className="text-sm font-medium text-muted-foreground">BCC</p>
                                        <p className="text-sm break-words">{bccRecipients.map((r) => r.email).join(", ")}</p>
                                    </div>
                                </div>
                            )}

                            <div className="flex items-start gap-3">
                                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
                                    <Calendar className="h-4 w-4 text-muted-foreground" />
                                </div>
                                <div className="flex-1 min-w-0 space-y-1">
                                    <p className="text-sm font-medium text-muted-foreground">
                                        {isReceived ? 'Received' : 'Sent'}
                                    </p>
                                    <p className="text-sm">
                                        {email.sentAt ? format(new Date(email.sentAt), "EEEE, MMMM d, yyyy 'at' h:mm a") : "N/A"}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <Separator />

                        <div className="space-y-1">
                            <p className="text-sm font-medium px-2">Subject</p>
                            <DialogHeader className="min-w-0 overflow-hidden px-2">
                                <DialogTitle className="text-2xl font-semibold break-words overflow-hidden">{email.subject || "(No Subject)"}</DialogTitle>
                            </DialogHeader>
                        </div>

                        <Separator />

                        {/* Email Body */}
                        <div className="space-y-2">
                            <h3 className="text-sm font-semibold">Message</h3>
                            <div className="rounded-lg bg-muted/50 p-4 overflow-x-hidden w-0 min-w-full">
                                {email.bodyHtml ? (
                                    <div
                                        className="prose prose-sm dark:prose-invert max-w-none break-words overflow-hidden [&_*]:max-w-full [&_img]:max-w-full [&_table]:max-w-full [&_table]:overflow-x-auto [&_table]:block [&_pre]:max-w-full [&_pre]:overflow-x-auto [&_pre]:whitespace-pre-wrap"
                                        // Defense-in-depth: bodies are sanitized server-side at ingestion,
                                        // but we sanitize again at render so no unsanitized markup can ever
                                        // reach the DOM (e.g. legacy rows or a future non-sanitizing path).
                                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(email.bodyHtml) }}
                                    />
                                ) : email.bodyText ? (
                                    <p className="whitespace-pre-wrap text-sm leading-relaxed break-words overflow-hidden">{email.bodyText}</p>
                                ) : (
                                    <p className="text-sm text-muted-foreground italic">(No message content)</p>
                                )}
                            </div>
                        </div>

                        {email.attachments?.length > 0 && (
                            <div className="space-y-2">
                                <h3 className="text-sm font-semibold">Attachments ({email.attachments.length})</h3>
                                <ul className="divide-y rounded-md border" aria-label="Email attachments">
                                    {email.attachments.map((attachment) => (
                                        <li key={attachment.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                                            <span className="min-w-0 flex-1 truncate" title={attachment.filename}>{attachment.filename}</span>
                                            <span className="shrink-0 text-xs text-muted-foreground">{formatAttachmentSize(attachment.sizeBytes)}</span>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleDownloadAttachment(attachment.id, attachment.filename)}
                                                disabled={!onDownloadAttachment || downloadingAttachmentId !== null}
                                                aria-label={`Download ${attachment.filename}`}
                                                title={`Download ${attachment.filename}`}
                                            >
                                                {downloadingAttachmentId === attachment.id
                                                    ? <Loader2 className="h-4 w-4 animate-spin" />
                                                    : <Download className="h-4 w-4" />}
                                            </Button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Error Message */}
                        {email.errorMessage && (
                            <>
                                <Separator />
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                        <XCircle className="h-4 w-4 text-destructive" />
                                        <h3 className="text-sm font-semibold text-destructive">Error</h3>
                                    </div>
                                    <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4">
                                        <p className="text-sm text-destructive">{email.errorMessage}</p>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Reply Section — only for received emails when onReply is provided */}
                        {canReply && onReply && (
                            <>
                                <Separator />
                                {!showReplyForm ? (
                                    <Button
                                        variant="outline"
                                        className="w-full gap-2"
                                        onClick={() => setShowReplyForm(true)}
                                    >
                                        <Reply className="h-4 w-4" />
                                        Reply to {replyRecipientLabel}
                                    </Button>
                                ) : (
                                    <div className="space-y-3 rounded-lg border p-4">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2 text-sm font-medium">
                                                <Reply className="h-4 w-4" />
                                                Replying to {replyRecipientLabel}
                                            </div>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => {
                                                    setShowReplyForm(false);
                                                    setReplyBody("");
                                                }}
                                                disabled={isReplying}
                                                aria-label="Collapse reply form"
                                                title="Collapse reply form"
                                            >
                                                <ChevronDown className="h-4 w-4" />
                                            </Button>
                                        </div>
                                        <p className="text-xs text-muted-foreground break-words">
                                            To: {replyRecipientLabel} · Subject: {replySubject}
                                        </p>
                                        <Textarea
                                            placeholder="Type your reply..."
                                            className="min-h-[120px] resize-none"
                                            value={replyBody}
                                            onChange={(e) => setReplyBody(e.target.value)}
                                            disabled={isReplying}
                                            autoFocus
                                        />
                                        <div className="flex justify-end gap-2">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => {
                                                    setShowReplyForm(false);
                                                    setReplyBody("");
                                                }}
                                                disabled={isReplying}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                size="sm"
                                                onClick={handleReply}
                                                disabled={!replyBody.trim() || isReplying}
                                                className="gap-2"
                                            >
                                                {isReplying ? (
                                                    <>
                                                        <Loader2 className="h-3 w-3 animate-spin" />
                                                        Sending...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Mail className="h-3 w-3" />
                                                        Send Reply
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
